/**
 * @file ProductReviewActions.tsx
 * @description Строка действий модератора под отзывом (phase19.10):
 * публикация, отклонение с причиной из пресетов, скрытие, публичный ответ
 * магазина, обезличивание автора и история модерации.
 *
 * Вызовы API — в useReviewModeration. Поля правки текста/оценки отзыва здесь
 * нет и быть не может — R3 docs/business/reviews.md.
 */

import { useState } from "react";

import { useAppShellOptional } from "../../../components/ui/AppShell";
import { Button } from "../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import {
  REJECT_REASON_PRESETS,
  type AdminProductReview,
} from "../../../lib/api-product-reviews";
import {
  ModerationHistoryPanel,
  RejectReasonPanel,
  ShopReplyPanel,
} from "./ProductReviewPanels";
import { useReviewModeration } from "./useReviewModeration";

interface ProductReviewActionsProps {
  review: AdminProductReview;
  /** Вызывается после успешного действия — родитель перезагружает список. */
  onChanged: () => void;
}

export function ProductReviewActions({ review, onChanged }: ProductReviewActionsProps) {
  const isMobile = useAppShellOptional()?.isMobile ?? false;
  const { busy, history, changeStatus, saveReply, anonymize, loadHistory } =
    useReviewModeration(review.id, onChanged);

  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState(review.shopReply ?? "");
  const [historyOpen, setHistoryOpen] = useState(false);

  async function reject(value: string) {
    if (await changeStatus("REJECTED", value)) {
      setReasonOpen(false);
      setReason("");
    }
  }

  async function reply(value: string) {
    if (await saveReply(value)) setReplyOpen(false);
  }

  function toggleHistory() {
    setHistoryOpen((open) => !open);
    if (!historyOpen) void loadHistory();
  }

  // На mobile кнопки растягиваются в две колонки и держат touch-target 44px.
  const btnClass = isMobile ? "h-11 grow basis-[45%]" : "";
  const btnSize = isMobile ? ("default" as const) : ("sm" as const);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {review.status !== "PUBLISHED" ? (
          <Button
            size={btnSize}
            className={btnClass}
            disabled={busy}
            onClick={() => void changeStatus("PUBLISHED")}
          >
            Опубликовать
          </Button>
        ) : null}

        {review.status !== "REJECTED" ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={(props) => (
                <Button
                  {...props}
                  variant="outline"
                  size={btnSize}
                  className={btnClass}
                  disabled={busy}
                >
                  Отклонить ▾
                </Button>
              )}
            />
            <DropdownMenuContent align="start">
              {REJECT_REASON_PRESETS.map((preset) => (
                <DropdownMenuItem key={preset} onClick={() => void reject(preset)}>
                  {preset}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setReasonOpen(true)}>
                Своя причина…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        {review.status === "PUBLISHED" ? (
          <Button
            variant="outline"
            size={btnSize}
            className={btnClass}
            disabled={busy}
            onClick={() => void changeStatus("HIDDEN")}
          >
            Скрыть
          </Button>
        ) : null}

        <Button
          variant="ghost"
          size={btnSize}
          className={btnClass}
          onClick={() => {
            setReplyDraft(review.shopReply ?? "");
            setReplyOpen((v) => !v);
          }}
        >
          Ответить
        </Button>
        <Button
          variant="ghost"
          size={btnSize}
          className={btnClass}
          onClick={toggleHistory}
        >
          История
        </Button>
        <Button
          variant="ghost"
          size={btnSize}
          className={btnClass}
          disabled={busy}
          onClick={() => void anonymize()}
        >
          Обезличить
        </Button>
      </div>

      {reasonOpen ? (
        <RejectReasonPanel
          value={reason}
          onChange={setReason}
          onSubmit={() => void reject(reason.trim())}
          onCancel={() => setReasonOpen(false)}
          busy={busy}
          btnSize={btnSize}
          btnClass={btnClass}
        />
      ) : null}

      {replyOpen ? (
        <ShopReplyPanel
          value={replyDraft}
          onChange={setReplyDraft}
          onSave={() => void reply(replyDraft.trim())}
          onDelete={() => void reply("")}
          canDelete={Boolean(review.shopReply)}
          busy={busy}
          btnSize={btnSize}
          btnClass={btnClass}
        />
      ) : null}

      {historyOpen ? <ModerationHistoryPanel entries={history} /> : null}
    </div>
  );
}
